import { OrderTypeEnum, type PositionSide } from '@kwenta/sdk/types'
import { type FC, memo, useCallback, useEffect } from 'react'
import styled, { css } from 'styled-components'

import ErrorView from 'components/ErrorView'
import { selectLeverageSide, selectTradeOrderType } from 'state/futures/common/selectors'
import {
	selectIsIsolatedMarginProvider,
	selectProfitCalculatorVisible,
} from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectPricesConnectionError } from 'state/prices/selectors'

import { changeLeverageSide, clearTradeInputs } from '../../../state/futures/actions'
import LeverageInput from '../LeverageInput'
import MarginInput from '../MarginInput'
import OrderSizing from '../OrderSizing'
import PositionButtons from '../PositionButtons'
import { ProfitCalculatorContent as ProfitCalculator } from '../ProfitCalculator/ProfitCalculator'

import { TradePanelOrderTypeSelector } from './OrderTypeSelector'
import SLTPInputs from './SLTPInputs'
import SubmitTrade from './SubmitTrade'
import { TradePanelContainer } from './TradePanelContainer'
import TradePanelPreview from './TradePanelPreview'
import TradePanelPriceInput from './TradePanelPriceInput'

type Props = {
	mobile?: boolean
	closeDrawer?: () => void
}

const TradePanel: FC<Props> = memo(({ mobile, closeDrawer }) => {
	const dispatch = useAppDispatch()

	const leverageSide = useAppSelector(selectLeverageSide)
	const pricesConnectionError = useAppSelector(selectPricesConnectionError)
	const orderType = useAppSelector(selectTradeOrderType)
	const profitCalculatorVisible = useAppSelector(selectProfitCalculatorVisible)
	const isIsolated = useAppSelector(selectIsIsolatedMarginProvider)

	useEffect(() => {
		dispatch(clearTradeInputs())
	}, [dispatch])

	const handleChangeSide = useCallback(
		(side: PositionSide) => {
			dispatch(changeLeverageSide(side))
		},
		[dispatch]
	)

	return (
		<StyledTradePanelContainer $mobile={mobile}>
			<>
				<PositionButtons
					selected={leverageSide}
					onSelect={handleChangeSide}
					mobile={mobile}
					closeDrawer={closeDrawer}
				/>

				<MainPanelContent $mobile={mobile} profitCalculatorVisible={profitCalculatorVisible}>
					{pricesConnectionError && <ErrorView message="Failed to connect to price feed." />}
					{profitCalculatorVisible && <ProfitCalculator />}
					{!profitCalculatorVisible && (
						<InputsContainer>
							<TradePanelOrderTypeSelector />
							{isIsolated && <MarginInput />}
							{orderType !== OrderTypeEnum.MARKET && <TradePanelPriceInput />}
							<OrderSizing />
							<LeverageInput />
							<SLTPInputs />
							<SubmitTrade />
							<TradePanelPreview />
						</InputsContainer>
					)}
				</MainPanelContent>
			</>
		</StyledTradePanelContainer>
	)
})

const MainPanelContent = styled.div<{ profitCalculatorVisible: boolean; $mobile?: boolean }>`
	padding: 0 15px;
	padding-top: 16px;
	${({ profitCalculatorVisible }) => !profitCalculatorVisible && 'padding-bottom: 40px'};
	width: 100%;
	height: 100%;
	overflow: scroll;
	position: relative;
	background-color: ${(props) =>
		props.theme.colors.selectedTheme.newTheme.containers.secondary.background};
	${(props) =>
		props.$mobile &&
		css`
			padding: 65px 15px 15px;
		`}
`

const InputsContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 15px;
`

const StyledTradePanelContainer = styled(TradePanelContainer)`
	height: 100%;
	overflow-y: scroll;
	display: flex;
	flex-direction: column;
`

export default TradePanel
